import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/primary-contacts.ts'
import { ContactEditOverlay } from '#app/routes/mocra+/_form+/components/contacts/edit.tsx'
import { ContactWithIdSchema } from '#app/routes/mocra+/_form+/components/contacts/schema.ts'
import {
	createEmptyContactDraft,
	type ContactCandidate,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { combineCandidateLists } from '#app/routes/mocra+/_form+/components/contacts/utils.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import {
	ContactAssignmentsSection,
	type ContactAssignmentSectionConfig,
} from '#app/routes/mocra+/_form+/primary-contacts/contact-assignments-section.tsx'
import { createSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Primary Contacts' },
]

const SECTION_KEY = 'primaryContacts'
const STEP_ID = 'primary-contacts'

const PrimaryContactSchema = ContactWithIdSchema.extend({
	title: z
		.string({
			required_error: 'Enter the contact title.',
		})
		.min(1, 'Enter the contact title.'),
	phone: z
		.string({
			required_error: 'Enter the contact phone number.',
		})
		.min(1, 'Enter the contact phone number.'),
})

const AssignmentField = z.string().min(1).or(z.literal('')).catch('')

const RoleAssignmentsSchema = z.object({
	regulatoryLeadId: AssignmentField,
	qualityLeadId: AssignmentField,
	legalCounselId: AssignmentField,
	operationsLeadId: AssignmentField,
	medicalReviewerId: AssignmentField,
})

const PrimaryContactsSchema = z
	.object({
		contacts: z.array(PrimaryContactSchema).default([]),
		assignments: RoleAssignmentsSchema,
	})
	.superRefine((value, ctx) => {
		const contacts = new Map(
			value.contacts.map((contact) => [contact.id, contact]),
		)
		for (const [key, contactId] of Object.entries(value.assignments)) {
			if (contactId && !contacts.has(contactId)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['assignments', key],
					message: 'Select a contact saved in this section.',
				})
			}
		}
	})

export type PrimaryContactsFormValues = z.infer<typeof PrimaryContactsSchema>

type PrimaryContact = PrimaryContactsFormValues['contacts'][number]
export type PrimaryContactAssignments = PrimaryContactsFormValues['assignments']

const DEFAULT_ASSIGNMENTS: PrimaryContactAssignments = {
	regulatoryLeadId: '',
	qualityLeadId: '',
	legalCounselId: '',
	operationsLeadId: '',
	medicalReviewerId: '',
}

const DEFAULT_VALUES: PrimaryContactsFormValues = {
	contacts: [],
	assignments: { ...DEFAULT_ASSIGNMENTS },
}

const PRIMARY_CONTACTS_RESOURCES = {
	regulatory: createSectionResources({
		mocraSections: ['overview', '605', '607', '608', '609', '610', '611'],
		templateSections: ['overview', '605', '607', '608', '609', '610', '611'],
	}),
	quality: createSectionResources({
		mocraSections: ['605', '607', '609', '610', '611'],
		templateSections: ['605', '607', '609', '610', '611'],
	}),
	legal: createSectionResources({
		mocraSections: ['605', '610', '611'],
		templateSections: ['605', '610', '611'],
	}),
	operations: createSectionResources({
		mocraSections: ['606', '611'],
		templateSections: ['606', '611'],
	}),
	medical: createSectionResources({
		mocraSections: ['605'],
		templateSections: ['605'],
	}),
}

const ROLE_SECTIONS = [
	{
		key: 'regulatoryLeadId',
		title: 'Regulatory lead',
		description:
			'Owns regulatory filings, FDA correspondence, and serious adverse event submissions.',
		resourceKey: 'regulatory',
		addLabel: 'Add contact',
	},
	{
		key: 'qualityLeadId',
		title: 'Quality lead',
		description:
			'Oversees quality systems, inspections, and corrective actions across manufacturing.',
		resourceKey: 'quality',
		addLabel: 'Add contact',
	},
	{
		key: 'legalCounselId',
		title: 'Legal counsel',
		description:
			'Handles legal review of filings, enforcement actions, and regulator correspondence.',
		resourceKey: 'legal',
		addLabel: 'Add contact',
	},
	{
		key: 'operationsLeadId',
		title: 'Operations lead',
		description:
			'Coordinates recall execution, retailer notifications, and supply chain impact.',
		resourceKey: 'operations',
		addLabel: 'Add contact',
	},
	{
		key: 'medicalReviewerId',
		title: 'Medical reviewer',
		description:
			'Provides clinical review for adverse event escalation and submission decisions.',
		resourceKey: 'medical',
		addLabel: 'Add contact',
	},
] as const satisfies readonly {
	readonly key: keyof PrimaryContactAssignments
	readonly title: string
	readonly description: string
	readonly resourceKey: keyof typeof PRIMARY_CONTACTS_RESOURCES
	readonly addLabel: string
}[]

export default function PrimaryContacts() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [editContactId, setEditContactId] = useState<string | null>(null)
	const [editingRole, setEditingRole] = useState<
		keyof PrimaryContactAssignments | null
	>(null)

	const {
		values,
		setValues,
		setFieldValue,
		submit,
		status,
		hasStoredValue,
		clear,
	} = useSectionForm({
		section: SECTION_KEY,
		schema: PrimaryContactsSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const contacts = useMemo(() => values.contacts ?? [], [values.contacts])

	const contactMap = useMemo(
		() => new Map(contacts.map((contact) => [contact.id, contact])),
		[contacts],
	)

	const assignments = useMemo(
		() => ({
			...DEFAULT_ASSIGNMENTS,
			...(values.assignments ?? DEFAULT_ASSIGNMENTS),
		}),
		[values.assignments],
	)

	const assignmentSections = useMemo<readonly ContactAssignmentSectionConfig[]>(
		() =>
			ROLE_SECTIONS.map((roleSection) => {
				const resources = PRIMARY_CONTACTS_RESOURCES[roleSection.resourceKey]
				const assignedId = assignments[roleSection.key] ?? ''
				const assignedContact = assignedId
					? (contactMap.get(assignedId) ?? null)
					: null
				return {
					key: roleSection.key,
					title: roleSection.title,
					description: roleSection.description,
					addLabel: roleSection.addLabel,
					resources,
					assignedId,
					assignedContact,
				}
			}),
		[assignments, contactMap],
	)
	const {
		mergeContacts: mergeDirectoryContacts,
		candidates: directoryCandidates,
	} = useContactDirectory()

	const mergeSignatureRef = useRef<string | null>(null)

	useEffect(() => {
		if (contacts.length === 0) return
		const signature = contacts
			.map(
				(contact) =>
					`${contact.id ?? ''}|${contact.name}|${contact.email}|${contact.phone}|${contact.title}`,
			)
			.join('||')
		if (mergeSignatureRef.current === signature) return
		mergeSignatureRef.current = signature
		mergeDirectoryContacts(contacts, { source: 'Primary contacts' })
	}, [contacts, mergeDirectoryContacts])

	const sectionCandidates = useMemo<ContactCandidate[]>(() => {
		return contacts.map((contact) => ({
			id: contact.id,
			name: contact.name,
			email: contact.email,
			phone: contact.phone,
			source: contact.title,
		}))
	}, [contacts])

	const contactCandidates = useMemo<ContactCandidate[]>(() => {
		return combineCandidateLists(directoryCandidates, sectionCandidates)
	}, [directoryCandidates, sectionCandidates])

	const updateStateWithContact = (
		draft: ContactDraft,
		assignmentUpdater?: (
			assignments: PrimaryContactAssignments,
			contact: PrimaryContact,
		) => PrimaryContactAssignments,
	) => {
		const sanitized = sanitizeContactDraft(draft)
		const normalizedContact = createContactFromDraft(sanitized)
		setValues((current) => {
			const normalizedState = normalizeState(current)
			const existingIndex = normalizedState.contacts.findIndex(
				(contact) => contact.id === normalizedContact.id,
			)
			const nextContacts =
				existingIndex >= 0
					? normalizedState.contacts.map((contact, index) =>
							index === existingIndex ? normalizedContact : contact,
						)
					: [...normalizedState.contacts, normalizedContact]
			const nextAssignments = assignmentUpdater
				? assignmentUpdater(normalizedState.assignments, normalizedContact)
				: normalizedState.assignments
			return {
				contacts: nextContacts,
				assignments: nextAssignments,
			}
		})
		return normalizedContact
	}

	const handleEditContactSubmit = (draft: ContactDraft) => {
		updateStateWithContact(
			draft,
			editingRole
				? (currentAssignments, contact) => {
						if (currentAssignments[editingRole] === contact.id) {
							return currentAssignments
						}
						return {
							...currentAssignments,
							[editingRole]: contact.id,
						}
					}
				: undefined,
		)
		setEditContactId(null)
		setEditingRole(null)
	}

	const assignContactToRole = (
		role: keyof PrimaryContactAssignments,
		draft: ContactDraft,
	) => {
		updateStateWithContact(draft, (currentAssignments, contact) => ({
			...currentAssignments,
			[role]: contact.id,
		}))
	}

	const handleClearAssignment = (role: keyof PrimaryContactAssignments) => {
		setFieldValue('assignments', {
			...assignments,
			[role]: '',
		})
	}

	const editingContact = editContactId
		? (contactMap.get(editContactId) ?? null)
		: null

	return (
		<Page
			title="Primary Contacts"
			description="Assign the people responsible for regulatory correspondence, quality leadership, legal review, operations, and medical oversight."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<ContactEditOverlay
					open={editContactId !== null}
					onOpenChange={(open) => {
						if (!open) {
							setEditContactId(null)
							setEditingRole(null)
						}
					}}
					onSubmit={handleEditContactSubmit}
					initialValue={
						editingContact
							? {
									id: editingContact.id,
									title: editingContact.title,
									name: editingContact.name,
									email: editingContact.email,
									phone: editingContact.phone,
								}
							: createEmptyContactDraft()
					}
					candidates={contactCandidates}
					directorySource="Primary contacts"
					onClosed={() => {
						setEditContactId(null)
						setEditingRole(null)
					}}
				/>

				<ContactAssignmentsSection
					sections={assignmentSections}
					contactCandidates={contactCandidates}
					onAssign={assignContactToRole}
					onEdit={(role, contactId) => {
						setEditingRole(role)
						setEditContactId(contactId)
					}}
					onClear={handleClearAssignment}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}
				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function sanitizeContactDraft(draft: ContactDraft): ContactDraft {
	return {
		id: draft.id,
		title: draft.title.trim(),
		name: draft.name.trim(),
		email: draft.email.trim(),
		phone: draft.phone.trim(),
	}
}

function normalizeState(
	state: PrimaryContactsFormValues,
): PrimaryContactsFormValues {
	return {
		contacts: [...(state.contacts ?? [])],
		assignments: {
			...DEFAULT_ASSIGNMENTS,
			...(state.assignments ?? {}),
		},
	}
}

function createContactFromDraft(draft: ContactDraft): PrimaryContact {
	return {
		id: draft.id && draft.id.length > 0 ? draft.id : generateContactId(),
		title: draft.title,
		name: draft.name,
		email: draft.email,
		phone: draft.phone,
	}
}

function generateContactId(): string {
	if (
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
	) {
		return crypto.randomUUID()
	}
	return `contact-${Math.random().toString(36).slice(2, 12)}`
}

function ignoredNormalizePrimaryContactsDraft(
	raw: unknown,
): PrimaryContactsFormValues | null {
	if (!raw || typeof raw !== 'object') {
		return {
			contacts: [],
			assignments: { ...DEFAULT_ASSIGNMENTS },
		}
	}

	if ('contacts' in (raw as Record<string, unknown>)) {
		const parsed = PrimaryContactsSchema.safeParse(raw)
		if (parsed.success) {
			const result = parsed.data
			return {
				contacts: result.contacts,
				assignments: {
					...DEFAULT_ASSIGNMENTS,
					...result.assignments,
				},
			}
		}
	}

	const legacy = raw as Record<string, unknown>
	const contacts: PrimaryContact[] = []
	const assignments: PrimaryContactAssignments = { ...DEFAULT_ASSIGNMENTS }

	const legacyFields = [
		{
			role: 'regulatoryLeadId' as const,
			nameKey: 'regulatoryLeadName',
			titleKey: 'regulatoryLeadTitle',
			emailKey: 'regulatoryLeadEmail',
			phoneKey: 'regulatoryLeadPhone',
		},
		{
			role: 'qualityLeadId' as const,
			nameKey: 'qualityLeadName',
			titleKey: 'qualityLeadTitle',
			emailKey: 'qualityLeadEmail',
			phoneKey: 'qualityLeadPhone',
		},
		{
			role: 'legalCounselId' as const,
			nameKey: 'legalCounselName',
			titleKey: 'legalCounselTitle',
			emailKey: 'legalCounselEmail',
			phoneKey: 'legalCounselPhone',
		},
		{
			role: 'operationsLeadId' as const,
			nameKey: 'operationsLeadName',
			titleKey: 'operationsLeadTitle',
			emailKey: 'operationsLeadEmail',
			phoneKey: 'operationsLeadPhone',
		},
		{
			role: 'medicalReviewerId' as const,
			nameKey: 'medicalReviewerName',
			titleKey: 'medicalReviewerTitle',
			emailKey: 'medicalReviewerEmail',
			phoneKey: 'medicalReviewerPhone',
		},
	]

	for (const field of legacyFields) {
		const name = readLegacyString(legacy, field.nameKey)
		const title = readLegacyString(legacy, field.titleKey) || field.role
		const email = readLegacyString(legacy, field.emailKey)
		const phone = readLegacyString(legacy, field.phoneKey)
		if (!name && !email && !phone) continue
		const contact = createContactFromDraft({
			id: undefined,
			title,
			name,
			email,
			phone,
		})
		contacts.push(contact)
		assignments[field.role] = contact.id
	}

	return {
		contacts,
		assignments,
	}
}

function readLegacyString(
	source: Record<string, unknown>,
	key: string,
): string {
	const raw = source[key]
	return typeof raw === 'string' ? raw.trim() : ''
}
